// LandingPage — публичный лендинг /main (R68, V12 U-36-A).
// БЕЗ CRM-обвязки (нет AppShell/NavRail/ProtectedRoute): собственный sticky-header,
// секции по research-V12-landing-man §4 (0–11) и форма заявки (LandingLeadForm, R70).
// Формула: боль → решение → доказательство → выгоды → CTA (один фокус — заявка/презентация).

import { Box, Button, Chip, Container, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { Logo } from '@/components/Logo';
import { JivoWidget } from '@/components/JivoWidget';
import { LandingLeadForm } from '@/components/LandingLeadForm';
import { demoPassword, demoUsername, publicOrigin } from '@/config/publicEnv';

const PRESENTATION_PPTX = '/presentations/sled-crm-presentation.pptx';
const PRESENTATION_PDF = '/presentations/sled-crm-presentation.pdf';

/** Модули CRM (research §4.4: результаты, не фичи). */
const MODULES: Array<{ title: string; benefit: string }> = [
  { title: 'Сделки', benefit: 'Сделки не теряются: воронка со стадиями, плановая дата закрытия, окно «следующий контакт»' },
  { title: 'Задачи', benefit: 'Все дела на виду: сквозная нумерация, приоритеты, дедлайны до минуты, привязка к сделке' },
  { title: 'Контакты и компании', benefit: 'Клиент = карточка + история: телефон, email, сайт, ИНН, заметки, привязки к сделкам' },
  { title: 'Календарь', benefit: 'День под контролем: месяц/неделя/день, фильтры, события из сделок в один клик' },
  { title: 'Команда и права', benefit: 'Каждый видит своё: роли, департаменты, журнал аудита' },
  { title: 'Дашборд', benefit: 'Что происходит — видно сразу: KPI, задачи и сделки на сегодня, лента действий' },
];

/** Стек (research §4.5) — доказательства для ИТ-заказчика. */
const TECH_CHIPS = ['Java 21', 'Spring Boot', 'PostgreSQL 18', 'React 18', 'TypeScript', 'MUI'];

/** FAQ (research §4.10, 6 вопросов). */
const FAQ: Array<{ question: string; answer: string }> = [
  {
    question: 'Чем лучше Bitrix24?',
    answer: 'Тот же класс задач, но без внешнего вендора: свой API, свои данные, свой код, лёгкий сервер и запуск за 30 минут.',
  },
  {
    question: 'Сколько стоит?',
    answer: 'Мы внедряем её сначала у себя (внутренняя замена дефективной CRM). Для вас формат обсудим в заявке — это пилот, а не ценник в коробке.',
  },
  {
    question: 'Как быстро внедрим?',
    answer: 'Запуск — 30 минут на лёгком сервере (Docker + nginx + HTTPS). Перенос данных — по шагам вместе с вами.',
  },
  {
    question: 'Что с интеграцией JivoChat?',
    answer: 'В roadmap: чат → лид → сделка. Напишите в заявке — расскажем, что уже есть и что планируем.',
  },
  {
    question: 'Открыт ли код?',
    answer: 'Код открыт в приватных GitLab-репозиториях — доступ по инвайту. Разработка по TDD: ~1380 backend-тестов, 446+ фронтенд-тестов.',
  },
  {
    question: 'Как получить доступ?',
    answer: 'Оставьте заявку: пришлём презентацию, покажем демо-стенд и по договорённости — приглашение в репозитории.',
  },
];

/** Sticky-шапка лендинга: логотип + название + единственный CTA (research §4.0). */
function LandingHeader(): JSX.Element {
  return (
    <Box component="header" sx={{ position: 'sticky', top: 0, zIndex: 10, bgcolor: 'background.default', borderBottom: 1, borderColor: 'divider' }}>
      <Container maxWidth="lg">
        <Stack direction="row" alignItems="center" spacing={2} sx={{ py: 1.5 }}>
          <Logo size={40} alt="Упряжка logo" />
          <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
            Упряжка
          </Typography>
          <Button component="a" href={PRESENTATION_PPTX} download variant="contained">
            Скачать презентацию
          </Button>
        </Stack>
      </Container>
    </Box>
  );
}

/** Hero: заголовок-ценность за 5 секунд (research §4.1) + 2 CTA. */
function HeroSection(): JSX.Element {
  return (
    <Box component="section" data-testid="landing-hero" sx={{ py: { xs: 6, md: 10 } }}>
      <Container maxWidth="lg">
        <Stack spacing={3} alignItems="flex-start">
          <Typography variant="h1" component="h1" sx={{ fontSize: { xs: '2rem', md: '3rem' }, fontWeight: 700, maxWidth: 800 }}>
            CRM от продажников JivoChat — для продажников JivoChat
          </Typography>
          <Typography variant="h6" component="p" color="text.secondary" sx={{ maxWidth: 700 }}>
            Мы заменили дефективную CRM отдела продаж: нормальные фильтры, контроль сделок — работаем не вслепую.
            Система, сделанная своими руками, — своя и для вас.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button component="a" href={publicOrigin} target="_blank" rel="noopener noreferrer" size="large" variant="contained">
              Открыть демо-стенд
            </Button>
            <Button component="a" href="#lead-form" size="large" variant="outlined">
              Оставить заявку
            </Button>
          </Stack>
          <Stack component="ul" direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pl: 0, listStyle: 'none' }}>
            {['живой демо-стенд без регистрации', 'презентация в 1 клик', '10–100 сотрудников, один сервер 2 vCPU', 'код наш, данные наши — без вендора'].map(
              (bullet) => (
                <Typography component="li" key={bullet} variant="body2" color="text.secondary">
                  — {bullet}
                </Typography>
              ),
            )}
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

/** Боль → решение: 3 карточки (research §4.2) + цитата автора (research §4.8). */
function PainSection(): JSX.Element {
  return (
    <Box component="section" data-testid="pain-section" sx={{ py: 8, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          <Typography variant="h2" component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 700 }}>
            Позавчера мы тоже работали вслепую
          </Typography>
          <Grid container spacing={3}>
            {[
              {
                title: 'Проблема',
                text: 'Не те фильтры — сделки не найти. Воронка — «чёрная дыра»: сделки не проконтролировать. Доработка — каждый раз боль у отдела разработки.',
              },
              {
                title: 'Решение',
                text: 'Мы написали свою CRM под свои боли: живые фильтры, контроль сделок на каждом экране, работа не вслепую.',
              },
              {
                title: 'Упряжка',
                text: 'Решение для отделов продаж, которые устали от своей CRM: сделано продажником, работает в проде, код открыт.',
              },
            ].map((card) => (
              <Grid item xs={12} md={4} key={card.title}>
                <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                    {card.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {card.text}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
          <Paper elevation={1} sx={{ p: 3 }}>
            <Typography variant="h6" component="div" sx={{ fontStyle: 'italic' }}>
              «Я устала от нашей CRM… Поэтому написала свою»
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              — Татевик Седракян, менеджер по продажам JivoChat, автор «Упряжки»
            </Typography>
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

/** Что внутри: 6 модулей как результаты (research §4.4). */
function ModulesSection(): JSX.Element {
  return (
    <Box component="section" data-testid="modules-section" sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Stack spacing={4}>
          <Typography variant="h2" component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 700 }}>
            Что внутри
          </Typography>
          <Grid container spacing={3}>
            {MODULES.map((module) => (
              <Grid item xs={12} sm={6} md={4} key={module.title}>
                <Paper elevation={1} sx={{ p: 3, height: '100%' }}>
                  <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                    {module.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {module.benefit}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
}

/** Технологии и доказательства (research §4.5): стек + нагрузка + private-репозитории. */
function TechSection(): JSX.Element {
  return (
    <Box component="section" data-testid="tech-section" sx={{ py: 8, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Typography variant="h2" component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 700 }}>
            Технологии и доказательства
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {TECH_CHIPS.map((chip) => (
              <Chip key={chip} label={chip} variant="outlined" />
            ))}
          </Stack>
          <Typography variant="body1">Устанавливается за 30 минут на лёгком сервере — достаточно 2 vCPU.</Typography>
          <Typography variant="body1">
            Код — наш, данные — ваши: приватные репозитории, доступ по приглашению. 100% свой код, свой API.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

/** Демо-стенд: «результат в первый час» (research §4.6). Публичные креды — по ТЗ V12. */
function DemoSection(): JSX.Element {
  return (
    <Box component="section" data-testid="demo-section" sx={{ py: 8 }}>
      <Container maxWidth="lg">
        <Paper elevation={2} sx={{ p: 4 }}>
          <Stack spacing={2}>
            <Typography variant="h2" component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 700 }}>
              Хотите покрутить руками — без звонков и согласований
            </Typography>
            <Typography variant="body1">
              Демо-стенд: <Link href={publicOrigin} target="_blank" rel="noopener noreferrer">{publicOrigin}</Link>
            </Typography>
            <Typography variant="body1">Вход: {demoUsername} / {demoPassword}</Typography>
            <Typography variant="body2" color="text.secondary">
              Это живой прод-стенд — он же используется отделом продаж. Публичные креды допустимы (ТЗ V12); после
              входа пароль можно сменить.
            </Typography>
            <Box>
              <Button component="a" href={publicOrigin} target="_blank" rel="noopener noreferrer" variant="contained" size="large">
                Открыть демо-стенд
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}

/** Презентация — lead magnet (research §4.7): PPTX (обязателен) + PDF (если сгенерирован). */
function PresentationSection(): JSX.Element {
  return (
    <Box component="section" data-testid="presentation-section" sx={{ py: 8, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Typography variant="h2" component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 700 }}>
            Всё, что мы сделали, — в одной презентации
          </Typography>
          <Typography variant="body1">
            Титул → зачем своя CRM → 6 модулей → сделки и стадии → задачи → контакты → календарь → команда и RBAC →
            дашборд → стек → открытый код → roadmap. Скачайте и покажите своему отделу.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Button component="a" href={PRESENTATION_PPTX} download variant="contained" size="large">
              Скачать презентацию (PPTX)
            </Button>
            <Button component="a" href={PRESENTATION_PDF} download variant="outlined" size="large">
              Скачать презентацию (PDF)
            </Button>
          </Stack>
        </Stack>
      </Container>
    </Box>
  );
}

/** Форма заявки — главная конверсия (research §4.9): якорь #lead-form для CTA из hero. */
function LeadFormSection(): JSX.Element {
  return (
    <Box component="section" id="lead-form" sx={{ py: 8 }}>
      <Container maxWidth="md">
        <Stack spacing={3}>
          <Typography variant="h2" component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 700 }}>
            Заявка на интеграцию
          </Typography>
          <Typography variant="h6" component="p" color="text.secondary">
            Хотите так же — внедрим у вас. Оставьте заявку, и мы свяжемся с вами.
          </Typography>
          <Paper elevation={2} sx={{ p: { xs: 2, md: 4 } }}>
            <LandingLeadForm />
          </Paper>
        </Stack>
      </Container>
    </Box>
  );
}

/** FAQ — снятие возражений перед формой (research §4.10). */
function FaqSection(): JSX.Element {
  return (
    <Box component="section" data-testid="faq-section" sx={{ py: 8, bgcolor: 'background.paper' }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Typography variant="h2" component="h2" sx={{ fontSize: { xs: '1.6rem', md: '2.2rem' }, fontWeight: 700 }}>
            Частые вопросы
          </Typography>
          {FAQ.map((item) => (
            <Paper elevation={1} key={item.question} sx={{ p: 3 }}>
              <Typography variant="h6" component="h3" sx={{ mb: 1 }}>
                {item.question}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {item.answer}
              </Typography>
            </Paper>
          ))}
        </Stack>
      </Container>
    </Box>
  );
}

/** Футер (research §4.11): © 2026 JivoChat + креды демо. */
function LandingFooter(): JSX.Element {
  return (
    <Box component="footer" data-testid="landing-footer" sx={{ py: 4, borderTop: 1, borderColor: 'divider' }}>
      <Container maxWidth="lg">
        <Stack spacing={1}>
          <Typography variant="body2">© 2026 JivoChat. CRM «Упряжка» — сделана продажниками, для продажников.</Typography>
          <Typography variant="body2" color="text.secondary">
            <Link component={RouterLink} to="/privacy">
              Политика конфиденциальности
            </Link>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Демо: {publicOrigin} · {demoUsername} / {demoPassword} · Приватные репозитории GitLab — доступ по инвайту.
          </Typography>
        </Stack>
      </Container>
    </Box>
  );
}

/** Публичный лендинг /main (U-36-A): свой layout, без CRM-обвязки. */
export function LandingPage(): JSX.Element {
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <LandingHeader />
      <JivoWidget />
      <Box component="main">
        <HeroSection />
        <PainSection />
        <ModulesSection />
        <TechSection />
        <DemoSection />
        <PresentationSection />
        <LeadFormSection />
        <FaqSection />
      </Box>
      <LandingFooter />
    </Box>
  );
}
